import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MuiThemeProvider } from '@material-ui/core/styles';
import {
  Button,
  ButtonToolbar,
  Collapse,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Container,
  Row,
  Col,
  Card,
} from 'reactstrap';
import { Field, reduxForm, FormSection } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { DateRange } from '@material-ui/icons';
import { addDays, addHours } from 'date-fns';
import classnames from 'classnames';
import Scrollbar from 'react-smooth-scrollbar';
import { connect } from 'react-redux';
import SmartHtmlEditor from '../../../shared/components/form/SmartHtmlEditor';
import validate from './Validate';
import SmartCard from '../../../shared/components/SmartCard';
import renderField from '../../../shared/components/form/RenderField';
import {
  convertDateToHourStr,
  getDateWithoutTime,
} from '../../../shared/utils/smartutils';
import {
  ACTIVE_TAB,
  ATTENDANCE_CONFIRMATION_STATUS,
  DAYS_REPEAT_ARRAY,
  INOUT,
  WEEK_DAYS,
} from '../../../shared/utils/constants';
import DialogForFullScreenComponent from './DialogForFullScreenComponent';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import DateField from '../../../shared/components/form/DateField';
import Select from '../../../shared/components/form/Select';
import HourField from '../../../shared/components/form/HourField';
import AlertNotifications from './AlertNotifications';
import ColumnsForEventMetrics from './ColumnsForEventMetrics';
import MatTable from '../../../shared/components/material-table';
import EventMetricsChart from './EventMetricsChart';
import colors from '../../../shared/utils/colors';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';

const EventForm = (props) => {
  const {
    t,
    calendars,
    change,
    handleSubmit,
    dto,
    initialize,
    removeItemClickHandler,
    relatedObjects,
    loadEventMetrics,
    metricsData,
    loadCalendarAlerts,
    calendarNotifications,
    mappedChartData,
    onRelatedObjectsChange,
  } = props;

  const [daysRepeat, setDaysRepeat] = useState(DAYS_REPEAT_ARRAY);
  const [allDay, setAllDay] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [date, setDate] = useState({});
  const [repeatPeriod, setRepeatPeriod] = useState({});
  const [toggleDialog, setToggleDialog] = useState(false);
  const [forDelete, setForDelete] = useState(false);
  const [activeTab, setActiveTab] = useState(ACTIVE_TAB.ONE);

  const mappedLabels = [
    t(
      `screens.mnt_evento_calendario.confirmationStatus.${ATTENDANCE_CONFIRMATION_STATUS.NOT_ANSWERED}`
    ),
    t(
      `screens.mnt_evento_calendario.confirmationStatus.${ATTENDANCE_CONFIRMATION_STATUS.CONFIRMATION_YES}`
    ),
    t(
      `screens.mnt_evento_calendario.confirmationStatus.${ATTENDANCE_CONFIRMATION_STATUS.CONFIRMATION_NO}`
    ),
  ];

  const isNew = () => {
    return !!dto.id;
  };

  const toggleTab = (index) => {
    if (index === ACTIVE_TAB.TWO) {
      loadEventMetrics();
    }
    setActiveTab(index);
  };

  const validateDate = (newDate, id) => {
    let updatedDate = { ...date };
    if (id === 0) {
      updatedDate = {
        ...date,
        initialDate: newDate,
        finalDate: addHours(newDate, 1),
      };
    } else {
      updatedDate = { ...date, finalDate: newDate };
    }
    setDate(updatedDate);
  };

  const validateRepeatPeriod = (newDate, id) => {
    let updatedRepeatPeriod = { ...repeatPeriod };
    if (id === 0) {
      updatedRepeatPeriod = {
        ...repeatPeriod,
        inicialDateRepeat: newDate,
        finalDateRepeat: newDate,
      };
    } else {
      updatedRepeatPeriod = { ...repeatPeriod, finalDateRepeat: newDate };
    }
    setRepeatPeriod(updatedRepeatPeriod);
  };

  const onDaysRepeatChange = (value, index) => {
    const updatedDays = [...daysRepeat];
    if (value) {
      updatedDays[index] = INOUT.IN;
    } else {
      updatedDays[index] = INOUT.OUT;
    }
    setDaysRepeat(updatedDays);
  };

  const stringToArrayChar = (string) => {
    setDaysRepeat(string.split(';'));
  };

  const toggleDialogScreen = () => {
    setToggleDialog(!toggleDialog);
  };

  const onChangeNotifications = (value) => {
    change('lstNotifications', value);
  };

  const onContentChange = (value, textValue) => {
    change('communication.htmlContent', value);
    change('communication.textContent', textValue);
  };

  const setEventHeaderDaysRepeat = (value) => {
    change('daysRepeat', value);
  };

  const removeEventWithChildren = (communicationId) => {
    removeItemClickHandler(communicationId, true);
  };

  const removeEventWithoutChildren = (communicationId) => {
    removeItemClickHandler(communicationId, false);
  };

  const messageDialog = () => {
    let message = 'notifications.delete_modal';
    if (dto.repeat && forDelete) {
      message = 'notifications.delete-recurrent';
    }
    if (dto.repeat) {
      message = 'notifications.change-recurrent';
    }
    return message;
  };

  const onCalendarChange = (value) => {
    loadCalendarAlerts(value);
  };

  const isRecurrent = () => {
    if (dto.repeat) {
      toggleDialogScreen();
    } else {
      handleSubmit();
    }
  };

  const buttonsDialog = () => {
    if (dto.repeat && forDelete) {
      return [
        {
          order: 1,
          onClick: () => {
            setForDelete(false);
            toggleDialogScreen();
          },
          message: 'general.cancel',
        },
        {
          order: 2,
          color: 'warning',
          onClick: () => {
            toggleDialogScreen();
            removeEventWithChildren(dto.communication.id, true);
          },
          message: 'general.delete-all',
        },
        {
          order: 3,
          color: 'danger',
          onClick: () => {
            toggleDialogScreen();
            removeEventWithoutChildren(dto.communication.id, false);
          },
          message: 'general.delete-this-only',
        },
      ];
    }
    if (dto.repeat) {
      return [
        {
          order: 1,
          onClick: toggleDialogScreen,
          message: 'general.cancel',
        },
        {
          order: 2,
          color: 'warning',
          onClick: async () => {
            toggleDialogScreen();
            await change('saveAll', true);
            await handleSubmit();
          },
          message: 'general.change-all',
        },
        {
          order: 3,
          color: 'danger',
          onClick: () => {
            toggleDialogScreen();
            handleSubmit();
          },
          message: 'general.change-this-only',
        },
      ];
    }
    return [
      {
        order: 1,
        onClick: toggleDialogScreen,
        message: 'general.cancel',
      },
      {
        order: 2,
        color: 'warning',
        onClick: () => {
          toggleDialogScreen();
          removeEventWithoutChildren(dto.communication.id);
        },
        message: 'general.confirm',
      },
    ];
  };

  const hasAttendanceConfirmation = () => {
    return dto && dto.attendanceConfirmation;
  };

  const options = {
    plugins: {
      datalabels: false,
    },
    animation: {
      duration: 0,
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  useEffect(() => {
    setEventHeaderDaysRepeat(daysRepeat);
    return () => {
      setEventHeaderDaysRepeat('');
    };
  }, [daysRepeat]);

  useEffect(() => {
    const { initialDate, finalDate } = dto;
    initialize(dto);
    if (dto.allDay) {
      setAllDay(!allDay);
    }
    if (dto.initialHour && dto.finalHour) {
      setDate({
        initialDate: getDateWithoutTime(initialDate),
        finalDate: getDateWithoutTime(finalDate),
        initialHour: convertDateToHourStr(dto.start),
        finalHour: convertDateToHourStr(dto.end),
      });
    } else if (initialDate !== finalDate) {
      setDate({
        initialDate: getDateWithoutTime(initialDate),
        finalDate: getDateWithoutTime(finalDate),
        initialHour: convertDateToHourStr(dto.start),
        finalHour: convertDateToHourStr(dto.end),
      });
    } else {
      setDate({
        initialDate: initialDate
          ? getDateWithoutTime(initialDate)
          : initialDate,
        finalDate: finalDate ? getDateWithoutTime(finalDate) : finalDate,
        initialHour: convertDateToHourStr(dto.start),
        finalHour: convertDateToHourStr(addHours(dto.end, 1)),
      });
    }
    if (dto.repeat) {
      stringToArrayChar(dto.daysRepeat);
      setRepeat(!repeat);
      setRepeatPeriod({
        inicialDateRepeat: new Date(dto.inicialDateRepeat),
        finalDateRepeat: new Date(dto.finalDateRepeat),
      });
    }
  }, [dto]);

  useEffect(() => {
    if (repeat && Object.keys(repeatPeriod).length === 0) {
      setRepeatPeriod({
        inicialDateRepeat: addDays(date.initialDate, 1),
        finalDateRepeat: addDays(date.initialDate, 1),
      });
    }
    setEventHeaderDaysRepeat(daysRepeat);
  }, [repeat]);

  useEffect(() => {
    change('initialDate', date.initialDate);
    change('finalDate', date.finalDate);
    change('initialHour', date.start);
    change('finalHour', date.end);
    if (repeat) {
      if (repeatPeriod.inicialDateRepeat < date.initialDate) {
        setRepeatPeriod({
          inicialDateRepeat: addDays(date.initialDate, 1),
          finalDateRepeat: addDays(date.initialDate, 1),
        });
      }
    }
  }, [date]);

  useEffect(() => {
    change('inicialDateRepeat', repeatPeriod.inicialDateRepeat);
    change('finalDateRepeat', repeatPeriod.finalDateRepeat);
  }, [repeatPeriod]);

  return (
    <MuiThemeProvider>
      <DialogForFullScreenComponent
        toggleDialog={() => {
          setForDelete(false);
          toggleDialogScreen();
        }}
        isOpen={toggleDialog}
        color="warning"
        title="notifications.attention"
        message={messageDialog()}
        btns={buttonsDialog()}
      />
      <SmartCard>
        <Container>
          <div className="tabs tabs--justify tabs--bordered-bottom overflow-hidden">
            <div className="tabs__wrap">
              {hasAttendanceConfirmation() ? (
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === ACTIVE_TAB.ONE,
                      })}
                      onClick={() => {
                        toggleTab(ACTIVE_TAB.ONE);
                      }}
                    >
                      {t('screens.mnt_evento_calendario.title')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === ACTIVE_TAB.TWO,
                      })}
                      onClick={() => {
                        toggleTab(ACTIVE_TAB.TWO);
                      }}
                    >
                      {t('screens.mnt_evento_calendario.metrics')}
                    </NavLink>
                  </NavItem>
                </Nav>
              ) : null}
            </div>
            <TabContent activeTab={activeTab}>
              <TabPane tabId={ACTIVE_TAB.ONE} className="form">
                <form className="form" onSubmit={isRecurrent}>
                  <FormSection name="communication" className="form__section">
                    <div className="form__form-group">
                      <span className="modal__form__form-group-label">
                        {t('general.title')}
                      </span>
                      <div className="modal__form_form-group-field">
                        <Field
                          name="title"
                          type="text"
                          component={renderField}
                          maxLength="50"
                          placeholder={t('general.placeholders.type-here')}
                        />
                      </div>
                    </div>
                    <div className="form__form-group">
                      <span className="modal__form__form-group-label">
                        {t('general.description')}
                      </span>
                      <div className="form__form-group-field">
                        <Field
                          name="htmlContent"
                          component={SmartHtmlEditor}
                          onEditorChange={onContentChange}
                          htmlValue={dto.communication.htmlContent}
                          showTemplateCommunication={false}
                          foreignContent={dto.communication.htmlContent}
                        />
                      </div>
                    </div>
                  </FormSection>

                  <div className="form__form-group--3_columns pad-tb20">
                    <div className="form__form-group--2_columns d-flex flex-nowrap position-relative">
                      <div className="modal__form_form-group-field flex-2">
                        <Field
                          name="initialDate"
                          component={DateField}
                          selected={date.initialDate}
                          onChange={(values) => validateDate(values, 0)}
                        />
                        <DateRange className="modal__form__form-group-icon" />
                      </div>
                      {!allDay && (
                        <div className="modal__form_form-group-field flex-1">
                          <Field
                            name="initialHour"
                            component={HourField}
                            time={date.initialHour}
                          />
                        </div>
                      )}
                    </div>
                    <Label className="modal__form__form-group-label justify-content-center pad-lr10 mg-t8">
                      {t('general.until')}
                    </Label>
                    <div className="form__form-group--2_columns d-flex flex-nowrap position-relative">
                      <div className="modal__form_form-group-field flex-2">
                        <Field
                          name="finalDate"
                          component={DateField}
                          selected={date.finalDate}
                          minDate={date.initialDate}
                          onChange={(values) => validateDate(values, 1)}
                        />
                        <DateRange className="modal__form__form-group-icon" />
                      </div>
                      {!allDay && (
                        <div className="modal__form_form-group-field flex-1">
                          <Field
                            name="finalHour"
                            component={HourField}
                            time={date.finalHour}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal__form_form-group">
                    <div className="form__form-group-field">
                      <Label className="form__form-group-label pad-r5">
                        {t('calendar.all-day')}
                      </Label>
                      <Field
                        name="allDay"
                        component={renderCheckBoxField}
                        className="colored-click "
                        onChange={(event) => {
                          if (event.target) {
                            setAllDay(event.target.checked);
                          }
                        }}
                      />
                      <Label className="form__form-group-label pad-r5">
                        {t('calendar.request-presence')}
                      </Label>
                      <Field
                        name="attendanceConfirmation"
                        component={renderCheckBoxField}
                        className="colored-click"
                      />
                    </div>
                  </div>
                  <div className="form__form-group">
                    <span className="modal__form__form-group-label">
                      {t('general.calendar')}
                    </span>
                    <div className="modal__form_form-group-field">
                      <Field
                        name="calendar"
                        type="text"
                        component={SelectVirtualized}
                        isClearable={false}
                        onChange={onCalendarChange}
                        options={calendars}
                        placeholder={t(
                          'screens.mnt_evento_calendario.placeholders.select'
                        )}
                      />
                    </div>
                  </div>
                  <div className="form__form-group">
                    <span className="modal__form__form-group-label">
                      {t('general.location')}
                    </span>
                    <div className="modal__form_form-group-field">
                      <Field
                        name="local"
                        type="text"
                        component={renderField}
                        maxLength="50"
                        placeholder={t('general.placeholders.type-here')}
                      />
                    </div>
                  </div>
                  <div className="form__form-group">
                    <span className="modal__form__form-group-label">
                      {t('general.guest')}
                    </span>
                    <div className="form__form-group-field">
                      <Field
                        name="relatedObjects"
                        type="text"
                        component={Select}
                        isMulti
                        closeMenuOnSelect={false}
                        options={relatedObjects}
                        placeholder={t(
                          'screens.mnt_evento_calendario.placeholders.select'
                        )}
                        onChange={onRelatedObjectsChange}
                      />
                    </div>
                  </div>
                  <div className="form__form-group mg-b10">
                    <div className="form__form-group-field">
                      <Label className="generic__label">
                        {t('general.repeat')}
                      </Label>
                      <Field
                        name="repeat"
                        component={renderCheckBoxField}
                        className="colored-click"
                        onChange={(event) => {
                          if (event.target) {
                            setRepeat(event.target.checked);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <Collapse isOpen={repeat} className="width100">
                    <div className="form__form-group">
                      <span className="modal__form__form-group-label mg-b10">
                        {t('calendar.repeat-in')}
                      </span>
                      <div className="form__form-group-field pad-tb10">
                        {repeat &&
                          daysRepeat.map((day, index) => (
                            <div key={uuid()} className="d-flex">
                              <Label className="generic__label">
                                {t(
                                  `calendar.week-day-short.${WEEK_DAYS[index]}`
                                )}
                              </Label>
                              <Field
                                name={WEEK_DAYS[index]}
                                component={renderCheckBoxField}
                                className="colored-click"
                                defaultChecked={day === '1'}
                                onChange={(event) => {
                                  if (event.target) {
                                    onDaysRepeatChange(
                                      event.target.checked,
                                      index
                                    );
                                  }
                                }}
                              />
                            </div>
                          ))}
                      </div>
                      <div className="form__form-group--3_columns mg-b10">
                        <div className="form__form-group--2_columns">
                          <div className="modal__form_form-group-field flex-2">
                            <Field
                              name="inicialDateRepeat"
                              component={DateField}
                              minDate={addDays(date.start, 1)}
                              selected={repeatPeriod.inicialDateRepeat}
                              onChange={(values) =>
                                validateRepeatPeriod(values, 0)
                              }
                            />
                            <DateRange className="modal__form__form-group-icon" />
                          </div>
                        </div>
                        <Label className="modal__form__form-group-label justify-content-center pad-lr10 mg-t8">
                          {t('general.until')}
                        </Label>
                        <div className="form__form-group--2_columns">
                          <div className="modal__form_form-group-field flex-2">
                            <Field
                              name="finalDateRepeat"
                              component={DateField}
                              selected={repeatPeriod.finalDateRepeat}
                              minDate={repeatPeriod.inicialDateRepeat}
                              onChange={(values) =>
                                validateRepeatPeriod(values, 1)
                              }
                            />
                            <DateRange className="modal__form__form-group-icon" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Collapse>
                  <AlertNotifications
                    data={calendarNotifications}
                    onChangeAlertNotificationList={onChangeNotifications}
                  />
                </form>
                <ButtonToolbar className="form__button-toolbar">
                  {isNew() && (
                    <Button
                      color="danger"
                      onClick={() => {
                        setForDelete(!forDelete);
                        toggleDialogScreen();
                      }}
                    >
                      {t('general.delete')}
                    </Button>
                  )}
                  <Button color="success" onClick={isRecurrent}>
                    {t('general.save')}
                  </Button>
                </ButtonToolbar>
              </TabPane>
            </TabContent>
            {hasAttendanceConfirmation() ? (
              <TabContent activeTab={activeTab}>
                <TabPane tabId={ACTIVE_TAB.TWO}>
                  <Container>
                    <Row>
                      <Col md={12} lg={12} className="pr-0 pl-0">
                        <Card>
                          <div className="metrics-charts">
                            <EventMetricsChart
                              dataLabels={mappedLabels}
                              numbersChart={mappedChartData}
                              options={options}
                            />
                          </div>
                          <Scrollbar
                            className="scroll-legends"
                            alwaysShowTracks
                          >
                            <div className="justify-content-center chart-legends mt-3">
                              {mappedLabels
                                ? mappedLabels.map((item, index) => (
                                    <div key={uuid()}>
                                      <span
                                        style={{
                                          backgroundColor: colors[index],
                                        }}
                                      />
                                      {item}
                                    </div>
                                  ))
                                : null}
                            </div>
                          </Scrollbar>
                        </Card>
                      </Col>
                    </Row>
                    <Row className="Table">
                      <Col md={12} lg={12}>
                        <div className="material-table__wrap">
                          <MatTable
                            data={metricsData}
                            columns={ColumnsForEventMetrics(t)}
                            paging={false}
                            isFiltering
                          />
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </TabPane>
              </TabContent>
            ) : null}
          </div>
        </Container>
      </SmartCard>
    </MuiThemeProvider>
  );
};

EventForm.propTypes = {
  t: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  removeItemClickHandler: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  dto: PropTypes.shape({
    id: PropTypes.number,
    communication: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      htmlContent: PropTypes.string,
    }),
    start: PropTypes.number,
    end: PropTypes.number,
    repeat: PropTypes.bool,
    local: PropTypes.string,
    allDay: PropTypes.bool,
    initialDate: PropTypes.number,
    initialHour: PropTypes.string,
    finalDate: PropTypes.number,
    finalHour: PropTypes.string,
    daysRepeat: PropTypes.string,
    inicialDateRepeat: PropTypes.number,
    finalDateRepeat: PropTypes.number,
    attendanceConfirmation: PropTypes.bool,
  }).isRequired,
  initialize: PropTypes.func.isRequired,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  relatedObjects: PropTypes.arrayOf(
    PropTypes.shape(
      PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      })
    )
  ).isRequired,
  loadEventMetrics: PropTypes.func.isRequired,
  metricsData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loadCalendarAlerts: PropTypes.func.isRequired,
  onRelatedObjectsChange: PropTypes.func.isRequired,
  calendarNotifications: PropTypes.arrayOf(PropTypes.shape().isRequired)
    .isRequired,
  mappedChartData: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
};

export default withTranslation('common')(
  reduxForm({
    form: 'EventScreenForm',
    validate,
  })(
    connect((state) => ({
      loading: state.loading,
    }))(EventForm)
  )
);
